import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtCurrency, fmtPct, fmtHours } from "@/lib/calc";
import { Camera, Calendar, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const formatPeriod = (period) => {
  if (!period) return "";
  const [y, m] = period.split("-");
  const d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

const SnapshotsPage = () => {
  const [snaps, setSnaps] = useState([]);
  const [settings, setSettings] = useState({ currency: "USD" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, set] = await Promise.all([
        api.get("/snapshots"),
        api.get("/settings"),
      ]);
      setSnaps(s.data);
      setSettings(set.data);
    } catch {
      toast.error("Could not load snapshots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const takeSnapshot = async () => {
    setSaving(true);
    try {
      await api.post("/snapshots");
      toast.success("Snapshot saved");
      await load();
    } catch {
      toast.error("Could not save snapshot");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/snapshots/${confirmDel.id}`);
      toast.success("Snapshot deleted");
      await load();
    } catch {
      toast.error("Could not delete");
    } finally {
      setConfirmDel(null);
    }
  };

  const currency = settings?.currency || "USD";

  return (
    <div className="space-y-6" data-testid="snapshots-page">
      <div className="bg-white border border-[#E4DFD2] rounded-xl p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C]">
            Monthly snapshots
          </div>
          <div className="font-display text-xl font-semibold text-[#1F2326] mt-1">
            Save this month's numbers
          </div>
          <p className="text-sm text-[#69747C] mt-1 max-w-xl">
            Save this month's numbers. Then compare them with next month on the dashboard.
          </p>
        </div>
        <button
          onClick={takeSnapshot}
          disabled={saving}
          data-testid="take-snapshot-btn"
          className="bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] disabled:opacity-60 px-4 py-2 rounded-lg font-medium text-sm transition-colors inline-flex items-center gap-2 shadow-sm"
        >
          <Camera className="w-4 h-4" /> {saving ? "Saving..." : "Take snapshot"}
        </button>
      </div>

      <div className="bg-white border border-[#E4DFD2] rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-[#EDE7D6]">
          <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C]">
            Saved snapshots
          </div>
          <div className="font-display text-lg font-semibold text-[#1F2326]">
            History
          </div>
        </div>

        {loading ? (
          <div className="px-6 py-10 text-sm text-[#69747C] text-center">Loading.</div>
        ) : snaps.length === 0 ? (
          <div className="px-6 py-14 text-center">
            <Calendar className="w-8 h-8 text-[#C9C2B0] mx-auto mb-3" />
            <div className="text-sm text-[#69747C]">No snapshots yet.</div>
            <div className="text-xs text-[#A9A294] mt-1">
              Take one now to start tracking month over month.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-[#EDE7D6]" data-testid="snapshots-list">
            {snaps.map((s) => (
              <li
                key={s.id}
                data-testid={`snapshot-${s.id}`}
                className="px-6 py-4 flex flex-wrap items-center gap-4 group hover:bg-[#FBFAF6]/60 transition-colors"
              >
                <div className="min-w-[140px]">
                  <div className="font-display text-base font-semibold text-[#1F2326]">
                    {formatPeriod(s.period)}
                  </div>
                  <div className="text-xs text-[#69747C]">
                    {s.client_count} clients
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm flex-1">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C] font-semibold">
                      Revenue
                    </div>
                    <div className="font-medium text-[#1F2326] tabular-nums">
                      {fmtCurrency(s.metrics?.total_revenue || 0, currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C] font-semibold">
                      Profit
                    </div>
                    <div
                      className={`font-medium tabular-nums ${
                        (s.metrics?.gross_profit || 0) >= 0
                          ? "text-[#42624B]"
                          : "text-[#7E332D]"
                      }`}
                    >
                      {fmtCurrency(s.metrics?.gross_profit || 0, currency)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C] font-semibold">
                      Utilization
                    </div>
                    <div className="font-medium text-[#1F2326] tabular-nums">
                      {fmtPct(s.metrics?.avg_utilization || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C] font-semibold">
                      Hours
                    </div>
                    <div className="font-medium text-[#1F2326] tabular-nums">
                      {fmtHours(s.metrics?.total_logged_hours || 0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#69747C] font-semibold">
                      Health
                    </div>
                    <div className="font-medium text-[#1F2326]">
                      <span className="text-[#42624B]">
                        {s.metrics?.on_track_count ?? s.metrics?.healthy_count ?? 0}
                      </span>
                      <span className="text-[#A9A294]"> / </span>
                      <span className="text-[#7A5E1F]">
                        {s.metrics?.close_to_limit_count ?? s.metrics?.at_risk_count ?? 0}
                      </span>
                      <span className="text-[#A9A294]"> / </span>
                      <span className="text-[#7E332D]">
                        {s.metrics?.losing_money_count ?? s.metrics?.unprofitable_count ?? 0}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setConfirmDel(s)}
                  data-testid={`delete-snapshot-${s.id}`}
                  className="opacity-0 group-hover:opacity-100 text-[#A9A294] hover:text-[#C76055] transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
      >
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete snapshot for {confirmDel ? formatPeriod(confirmDel.period) : ""}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This removes the saved numbers for that month. It can not be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-[#C76055] text-white hover:bg-[#A04A40]"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SnapshotsPage;
